import { useEffect, useState } from 'react';
import {
  retrieveItem,
  retrieveUserItem,
  storeItem,
  storeUserItem,
  retrieveUserProviderItem,
  storeUserProviderItem,
} from '../lib/storage';

export default function useLocalStorageValue<T>(
  key: string,
  userId?: string,
  providerId?: string,
) {
  const [value, setValue] = useState<T | null>(() => {
    if (userId && providerId)
      return retrieveUserProviderItem<T>(userId, providerId, key);
    if (userId) return retrieveUserItem<T>(userId, key);
    return retrieveItem<T>(key);
  });

  useEffect(() => {
    const scopedKey =
      userId && providerId
        ? `${userId}:${providerId}:${key}`
        : userId
        ? `${userId}:${key}`
        : key;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== scopedKey) {
        return;
      }

      if (!event.newValue) {
        setValue(null);
        return;
      }
      // Another tab (or a different build of this app) wrote the value —
      // it may not be valid JSON for our schema, or valid JSON at all.
      try {
        setValue(JSON.parse(event.newValue));
      } catch (error) {
        console.warn(
          `Failed to parse localStorage value for key "${scopedKey}":`,
          error,
        );
        setValue(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key, userId, providerId]);

  const setLocalStorageValue = (newValue: T) => {
    if (userId && providerId) {
      storeUserProviderItem(userId, providerId, key, newValue);
    } else if (userId) {
      storeUserItem(userId, key, newValue);
    } else {
      storeItem(key, newValue);
    }
    setValue(newValue);
  };

  return [value, setLocalStorageValue] as const;
}
